import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send, CheckCircle, CloudUpload } from '@mui/icons-material';
import { sendDocumentApi } from '../services/api';

const SendDocument: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sendMutation = useMutation(
    ({ file, name, subject, message }: { file: File; name: string; subject?: string; message?: string }) =>
      sendDocumentApi.sendDocument(file, name, subject, message),
    {
      onSuccess: (data) => {
        setSuccess(`Document "${data.filename}" sent successfully! (${data.size_kb} KB)`);
        setError('');
        // Reset form
        setFile(null);
        setName('');
        setSubject('');
        setMessage('');
        // Reset file input
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // Hide success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      },
      onError: (error: any) => {
        setError(error.response?.data?.detail || 'Failed to send document');
        setSuccess('');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!file) {
      setError('Please select a file to send');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    sendMutation.mutate({ file, name, subject, message });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const formatFileSize = (bytes: number) => {
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Send Document via Email
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Upload a document to send directly via email to management.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <input
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.bmp"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileChange}
                required
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ mb: 2, py: 3 }}
                >
                  {file ? (
                    <>
                      {file.name} ({formatFileSize(file.size)})
                    </>
                  ) : (
                    'Select File to Send'
                  )}
                </Button>
              </label>

              <TextField
                fullWidth
                label="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                required
                disabled={sendMutation.isLoading}
                helperText="Enter your full name"
              />

              <TextField
                fullWidth
                label="Subject (Optional)"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                margin="normal"
                disabled={sendMutation.isLoading}
                helperText="Leave blank to use default subject"
              />

              <TextField
                fullWidth
                label="Message (Optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                margin="normal"
                multiline
                rows={4}
                disabled={sendMutation.isLoading}
                helperText="Add a message to include in the email"
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={sendMutation.isLoading ? <CircularProgress size={20} /> : <Send />}
                  disabled={sendMutation.isLoading || !file || !name.trim()}
                  fullWidth
                >
                  {sendMutation.isLoading ? 'Sending...' : 'Send Document'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setFile(null);
                    setName('');
                    setSubject('');
                    setMessage('');
                    setError('');
                    setSuccess('');
                    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  }}
                  disabled={sendMutation.isLoading}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> The document will be sent immediately to <strong>emerkys@gmail.com</strong> from <strong>midwestautomotivetrucking2@gmail.com</strong>.
              Accepted file types: PDF, Word, Excel, Images, and Text files.
            </Typography>
          </Alert>
        </Box>
      </Box>
    </Container>
  );
};

export default SendDocument;
